import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

// 元素曝光上报 hook，元素进入视口时触发上报，key 用于元素变化时触发重新曝光
const useExposeItem = ({
  key,
  ref,
  onExpose,
}: {
  key: string | number;
  ref: React.RefObject<HTMLElement>;
  onExpose: () => void;
}) => {
  const [exposed, setExposed] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !exposed) {
            onExpose(); // 曝光上报逻辑
            setExposed(true);
            if (ref.current) {
              observer.unobserve(ref.current);
            }
          }
        });
      },
      {
        root: null, // 视口
        rootMargin: '0px',
        threshold: 0.1, // 10%的元素内容进入视口时触发
      }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, key, onExpose]);
};

export { useExposeItem };

// 可以根据 key 多次监听同一个 ref 曝光
export const useControlledExposeItem = ({
  key,
  ref,
  onExpose,
}: {
  key: string | number;
  ref: React.RefObject<HTMLElement>;
  onExpose: () => void;
}) => {
  const exposedRef = useRef(false);
  const resetExpose = useCallback(() => {
    exposedRef.current = false;
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !exposedRef.current) {
            onExpose(); // 曝光上报逻辑
            exposedRef.current = true;
            if (ref.current) {
              observer.unobserve(ref.current);
            }
          }
        });
      },
      {
        root: null, // 视口
        rootMargin: '0px',
        threshold: 0.1, // 10%的元素内容进入视口时触发
      }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, key, onExpose]);

  return {
    resetExpose,
  };
};
